import { useMemo } from 'react';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import getLang from '../../../utils/getLang';
import FormattedDuration from 'I18n/components/FormattedDuration';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { List } from 'foundations-components/transitional/components/List';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import { ACRouterButton } from 'academy-common-lib/components/ACRouterButton';
import ACCertificatePreviewSection from '../../../components/section/ACCertificatePreviewSection';
import { isInternalContent } from '../../learningContent/utils';
import { useMediaQuery } from '../../../utils/Styling';
import UsageTracker from '../../../utils/UsageTracker';
import InstructorList from '../../../components/list/InstructorList';
import LearningContentCard from '../../learningContentCard/components/LearningContentCard';
import SectionHeading from '../../../components/SectionHeading';
import CardMenuIcon from '../../cardMenu/components/CardMenuIcon';
import useSearchParams from '../../../hooks/useSearchParams';
import { OverviewContent } from '../components/OverviewContent';
import { OverviewModalCurriculumSection } from '../components/OverviewModalCurriculumSection';
import { OverviewModalDescriptionSection } from '../components/OverviewModalDescriptionSection';
import { useOverviewNavigation } from '../overviewRouting';
import { useFullChirpTrack } from '../../tracks/tracksQueries';
import { LinkAssetTypename } from '../../tracks/tracksTypes';
import { isVideoAsset, maybeChirpTrackCardModelWithProgress } from '../../tracks/tracksUtils';
import { chirpLessonToLessonCardModel, mapProfessorDescriptions } from '../../lessons/lessonUtils';
import { getLanguagesFromLinkedContent } from '../../../utils/languageUtils';
import { useVideoContentCaptionCheck } from '../../video/videoHooks';
import { useGetAiAccessControls } from '../../aiControl/aiAccessQueries';
import { getTrackLessonPreAssessmentUrl } from '../overviewRoutingUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CardBody = ({
  lesson,
  index,
  language,
  trackId
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const handleStartLessonClick = () => UsageTracker.trackStandalone('Learning Center Interaction', {
    action: 'overview-modal-curriculum-click-start',
    contentType: 'LESSON',
    lessonId: lesson.id,
    lessonType: lesson.lessonType[0] ? readUnhandledEnumCase(lesson.lessonType[0]) : undefined,
    appearsIn: 'course-overview-curriculum',
    position: index + 1,
    name: lesson.name,
    language
  });
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    flexDirection: "column",
    gap: "300",
    children: [/*#__PURE__*/_jsx(View, {
      flexGrow: 1,
      children: lesson.description
    }), /*#__PURE__*/_jsx(View, {
      alignSelf: "end",
      children: /*#__PURE__*/_jsx(ACRouterButton, {
        "data-test-id": "start-lesson-button",
        onClick: handleStartLessonClick,
        role: "button",
        size: isMobile ? 'medium' : 'small',
        to: `/tracks/${trackId}/${lesson.id}?language=${language}`,
        use: "secondary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyComponents.overviewModal.viewLesson"
        })
      })
    })]
  });
};
const TrackOverviewModalContainer = ({
  isPlaylistOverview,
  language: languageFromProps,
  source,
  trackId
}) => {
  var _track$languageLinked, _track$icon;
  const [searchParams] = useSearchParams();
  const searchParamLanguage = searchParams.language;
  const {
    goTo
  } = useOverviewNavigation();
  const {
    isMobile
  } = useMediaQuery();
  const language = (languageFromProps || searchParamLanguage || getLang()).toUpperCase();
  const {
    trackLoading: loading,
    track,
    trackError: error
  } = useFullChirpTrack(trackId);
  const deprecated = track === null || track === void 0 ? void 0 : track.deprecated;
  const deprecationCta = track === null || track === void 0 ? void 0 : track.deprecationCta;
  const firstLesson = track === null || track === void 0 ? void 0 : track.lessons[0];
  const {
    allVideosHaveCaptions,
    loading: captionCheckLoading
  } = useVideoContentCaptionCheck({
    contents: firstLesson === null || firstLesson === void 0 ? void 0 : firstLesson.content
  });
  const {
    aiAccessControls
  } = useGetAiAccessControls();
  const isGenerativeAiEnabled = !!(aiAccessControls !== null && aiAccessControls !== void 0 && aiAccessControls.generativeAiEnabled);
  const enrollToUrl = useMemo(() => {
    if (!track || !firstLesson || !isGenerativeAiEnabled || !!track.introVideo || captionCheckLoading || !allVideosHaveCaptions || firstLesson.content.filter(isVideoAsset).length <= 1) {
      return undefined;
    }
    return getTrackLessonPreAssessmentUrl(trackId.toString(), firstLesson.id.toString());
  }, [track, firstLesson, isGenerativeAiEnabled, captionCheckLoading, allVideosHaveCaptions, trackId]);
  const trackAggregatedLessonContents = useMemo(() => {
    if (!track) return undefined;
    return track.lessons.reduce((aggregated, lesson) => {
      aggregated.push(...lesson.content);
      return aggregated;
    }, []);
  }, [track]);
  const professors = useMemo(() => {
    if (!trackAggregatedLessonContents) return [];
    const professorsById = {};
    trackAggregatedLessonContents.forEach(asset => {
      if (isVideoAsset(asset) && asset.professors) {
        asset.professors.forEach(professor => {
          professorsById[professor.id] = mapProfessorDescriptions(professor);
        });
      }
    });
    return Object.values(professorsById);
  }, [trackAggregatedLessonContents]);
  const availableLanguagesData = track && getLanguagesFromLinkedContent((_track$languageLinked = track.languageLinkedContent) === null || _track$languageLinked === void 0 ? void 0 : _track$languageLinked.languageToId, track.language);
  const curriculumLessons = useMemo(() => {
    if (!track || !track.lessons.length) return [];
    return track.lessons.filter(lesson => !lesson.content.every(content => content.__typename === LinkAssetTypename));
  }, [track]);
  const handleChangeSelectedLanguage = ({
    target: {
      value
    }
  }) => {
    var _track$languageLinked2;
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'overview-modal-change-language',
      appearsIn: source,
      trackId: track ? track.id : null,
      name: track ? track.name : null,
      previousLanguage: language,
      newLanguage: value
    });
    const languageToId = track === null || track === void 0 || (_track$languageLinked2 = track.languageLinkedContent) === null || _track$languageLinked2 === void 0 ? void 0 : _track$languageLinked2.languageToId;
    const linkedTrackLanguageToId = value.toUpperCase();
    const targetId = (languageToId === null || languageToId === void 0 ? void 0 : languageToId[linkedTrackLanguageToId]) || trackId;
    goTo({
      id: targetId,
      type: 'TRACK',
      language: value
    });
  };
  const handleEnroll = () => UsageTracker.trackStandalone('Learning Center Interaction', {
    action: 'overview-modal-enroll',
    appearsIn: source,
    contentLanguage: language,
    contentType: 'TRACK',
    lessonId: track ? track.id : null,
    name: track ? track.name : null,
    state: enrollToUrl ? 'pre_assessment_available' : 'pre_assessment_unavailable'
  });
  const handleOrganisationUrlClick = (professorId, organisation, organisationUrl) => () => UsageTracker.trackStandalone('Learning Center Interaction', {
    action: 'professor-organisation-click',
    contentType: 'COURSE',
    appearsIn: 'course-overview-curriculum',
    organisation,
    organisationUrl,
    professorId,
    trackId
  });
  const trackCardModel = maybeChirpTrackCardModelWithProgress(track);
  return /*#__PURE__*/_jsx(OverviewContent, {
    assetLanguage: language,
    backgroundColor: isPlaylistOverview ? 'fill-surface-default' : 'fill-surface-recessed',
    deprecated: !!deprecated,
    deprecationCta: deprecationCta || undefined,
    error: error,
    fallbackThumbnailUrl: track && track.previewThumbnailRectangleUrl,
    headerIcons: trackCardModel && /*#__PURE__*/_jsx(CardMenuIcon, {
      item: trackCardModel
    }),
    isInternalContent: trackCardModel ? isInternalContent(trackCardModel) : false,
    loading: loading,
    trackId: trackId,
    children: /*#__PURE__*/_jsxs(View, {
      paddingInline: isMobile ? '300' : '800',
      children: [/*#__PURE__*/_jsx(OverviewModalDescriptionSection, {
        availableLanguages: availableLanguagesData,
        contentList: trackAggregatedLessonContents,
        description: track && track.description,
        duration: track && /*#__PURE__*/_jsxs(List, {
          inline: true,
          use: "inline-divided",
          wrapChildren: true,
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyComponents.overviewModal.numLessons",
            options: {
              number: track.lessons.length
            }
          }), /*#__PURE__*/_jsx(FormattedDuration, {
            from: new Date().getTime() - track.duration * 1000
          })]
        }),
        enrollToUrl: enrollToUrl,
        hasCertificate: track && (track.certification || !!track.exam),
        hasStudyGuide: track && !!track.studyGuide,
        id: track === null || track === void 0 ? void 0 : track.id,
        isPlaylistOverview: isPlaylistOverview,
        onChangeSelectedLanguage: handleChangeSelectedLanguage,
        onEnroll: handleEnroll,
        selectedLanguage: language,
        title: track && track.name,
        type: "TRACK"
      }), track && (track.certification || !!track.exam) && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(SectionHeading, {
          paddingBlockEnd: "800",
          paddingBlockStart: "800",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyCommon.certificatePreview.title"
          })
        }), /*#__PURE__*/_jsx(ACCertificatePreviewSection, {
          borderColor: "border-core-subtle",
          borderRadius: "300",
          borderStyle: "solid",
          borderWidth: "100",
          color: "text-core-default",
          iconUrl: ((_track$icon = track.icon) === null || _track$icon === void 0 ? void 0 : _track$icon.location) || '',
          paddingInline: "700",
          title: track.name
        })]
      }), !!professors.length && /*#__PURE__*/_jsx(InstructorList, {
        backgroundColor: isPlaylistOverview ? 'fill-surface-recessed' : undefined,
        borderRadius: isPlaylistOverview ? '300' : undefined,
        handleOrganisationUrlClick: handleOrganisationUrlClick,
        language: language,
        marginBlockStart: isPlaylistOverview ? '600' : undefined,
        padding: isPlaylistOverview ? '800' : undefined,
        professors: professors
      }), !!curriculumLessons.length && /*#__PURE__*/_jsx(OverviewModalCurriculumSection, {
        isPlaylistOverview: isPlaylistOverview,
        children: curriculumLessons.map((lesson, index) => /*#__PURE__*/_jsx(View, {
          color: "text-core-default",
          children: /*#__PURE__*/_jsx(LearningContentCard, {
            cardBody: /*#__PURE__*/_jsx(CardBody, {
              index: index,
              language: language,
              lesson: lesson,
              trackId: trackId
            }),
            disableLink: true,
            item: chirpLessonToLessonCardModel(lesson),
            overviewType: "course-overview-curriculum",
            position: index + 1,
            renderMenu: () => null,
            size: "small",
            underlineOnHover: false,
            use: "on-surface-recessed"
          }, lesson.id)
        }, lesson.id))
      })]
    })
  });
};
export default TrackOverviewModalContainer;